import { Alert, AppBar, Avatar, Box, Button, CircularProgress, Paper, TextField, Toolbar, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Logo from '../../assets/SRMD-Logo.png';
import { useTrackUsage } from '../../hooks/useTrackUsage';
import { publicEnglishForm } from '../../services/EnglishFormService';
import { vibrantColors } from '../../themes/VibrantTheme';
import { weekdayDay } from './calendarUi';

const CLOSED = 'This form is closed. Ask your coordinator for a new link.';
const NAME_KEY = 'englishForm.name';

// Each read and write can throw in a private window; a name just isn't remembered then
const rememberedName = () => {
    try {
        return localStorage.getItem(NAME_KEY) || '';
    } catch (e) {
        return '';
    }
};
const rememberName = (name) => {
    try {
        localStorage.setItem(NAME_KEY, name);
    } catch (e) {
        // Nothing to do
    }
};

const problem = (e) => {
    if (e?.response?.status === 404) return CLOSED;
    return e?.response?.data?.message || 'That did not go through. Check your connection and try again.';
};

const RequestForm = ({ service, name, setName, onSent }) => {
    const [title, setTitle] = useState('');
    const [neededBy, setNeededBy] = useState('');
    const [driveLink, setDriveLink] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const ready = title.trim() && neededBy && driveLink.trim() && name.trim();

    const send = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError('');
        try {
            const sent = await service.request({ title, neededBy, driveLink, name });
            rememberName(name);
            onSent(sent);
        } catch (err) {
            setError(problem(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <form onSubmit={send}>
            <TextField fullWidth required margin="normal" label="Video title" value={title} onChange={(e) => setTitle(e.target.value)} />
            <TextField
                fullWidth
                required
                margin="normal"
                type="date"
                label="Posting date"
                InputLabelProps={{ shrink: true }}
                value={neededBy}
                onChange={(e) => setNeededBy(e.target.value)}
            />
            <TextField
                fullWidth
                required
                margin="normal"
                label="Google Drive link"
                placeholder="https://drive.google.com/file/d/..."
                helperText="Share the file as Anyone with the link, or it can't be picked up."
                value={driveLink}
                onChange={(e) => setDriveLink(e.target.value)}
            />
            <TextField fullWidth required margin="normal" label="Your name" value={name} onChange={(e) => setName(e.target.value)} />
            {error && (
                <Alert severity="error" sx={{ mt: 1 }}>
                    {error}
                </Alert>
            )}
            <Button type="submit" variant="contained" fullWidth sx={{ mt: 2, minHeight: 44 }} disabled={busy || !ready}>
                Send request
            </Button>
        </form>
    );
};

const SrtForm = ({ service, name, setName, onSent }) => {
    const [title, setTitle] = useState('');
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const ready = title.trim() && file && name.trim();

    const send = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError('');
        try {
            const sent = await service.srt(file, title, name);
            rememberName(name);
            onSent(sent);
        } catch (err) {
            setError(problem(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <form onSubmit={send}>
            <TextField fullWidth required margin="normal" label="Video title" value={title} onChange={(e) => setTitle(e.target.value)} />
            <Box sx={{ mt: 2, mb: 1 }}>
                <Button component="label" variant="outlined" sx={{ minHeight: 44 }}>
                    Choose SRT file
                    <input hidden type="file" accept=".srt" onChange={(e) => setFile(e.target.files[0] || null)} />
                </Button>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>
                    {file ? file.name : '.srt, up to 5 MB'}
                </Typography>
            </Box>
            <TextField fullWidth required margin="normal" label="Your name" value={name} onChange={(e) => setName(e.target.value)} />
            {error && (
                <Alert severity="error" sx={{ mt: 1 }}>
                    {error}
                </Alert>
            )}
            <Button type="submit" variant="contained" fullWidth sx={{ mt: 2, minHeight: 44 }} disabled={busy || !ready}>
                Send SRT
            </Button>
        </form>
    );
};

// The app's PageHeader bar without the parts that need a login (tasks link, user menu)
const FormHeader = () => {
    const ink = vibrantColors[useTheme().palette.mode === 'dark' ? 'dark' : 'light'];
    return (
        <AppBar
            position="relative"
            elevation={0}
            sx={{ backgroundColor: ink.header, backgroundImage: 'none', color: ink.headerInk, boxShadow: '0 4px 10px rgba(0, 0, 0, 0.12)' }}
        >
            <Toolbar>
                <Avatar src={Logo} alt="SRMD Logo" sx={{ width: 40, height: 40, mr: 1 }} />
                <Typography variant="h5" component="div" sx={{ fontFamily: '"Marcellus", serif', fontWeight: 400 }}>
                    AmrutVaani
                </Typography>
            </Toolbar>
        </AppBar>
    );
};

/** /r/:token: AV's English subtitle request and SRT forms, with no login (rev6 Part 11, 6j-2, 6j-3). */
const PublicEnglishFormPage = () => {
    const { token } = useParams();
    const service = useMemo(() => publicEnglishForm(token), [token]);
    const [kind, setKind] = useState(null);
    const [closed, setClosed] = useState('');
    const [name, setName] = useState(rememberedName);
    const [sent, setSent] = useState(null);
    useTrackUsage('EnglishRequestForm', 'screen', 'tasks', kind === 'REQUEST');
    useTrackUsage('EnglishSrtForm', 'screen', 'tasks', kind === 'SRT');

    useEffect(() => {
        let live = true;
        service
            .open()
            .then((d) => live && setKind(d.kind))
            .catch((e) => live && setClosed(problem(e)));
        return () => {
            live = false;
        };
    }, [service]);

    const srt = kind === 'SRT';
    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
            <FormHeader />
            <Paper sx={{ maxWidth: 480, mx: { xs: 2, sm: 'auto' }, mt: { xs: 2, sm: 4 }, p: 3 }}>
                {closed && <Typography variant="body1">{closed}</Typography>}
                {!closed && !kind && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                )}
                {kind && sent && (
                    <Box role="status">
                        <Typography variant="h6">{srt ? 'SRT saved' : 'Request received'}</Typography>
                        <Typography variant="body2" sx={{ mt: 1, overflowWrap: 'anywhere' }}>
                            {srt ? `Saved as ${sent.savedAs}` : `${sent.title}, posting on ${weekdayDay(sent.neededBy)}`}
                        </Typography>
                        <Button sx={{ mt: 2, minHeight: 44 }} onClick={() => setSent(null)}>
                            Send another
                        </Button>
                    </Box>
                )}
                {kind && !sent && (
                    <>
                        <Typography variant="h6">{srt ? 'Send an English SRT' : 'Request English subtitles'}</Typography>
                        {srt ? (
                            <SrtForm service={service} name={name} setName={setName} onSent={setSent} />
                        ) : (
                            <RequestForm service={service} name={name} setName={setName} onSent={setSent} />
                        )}
                    </>
                )}
            </Paper>
        </Box>
    );
};

export default PublicEnglishFormPage;
